/**
 * Focus plumbing for {@link UserMenu}.
 *
 * Items are found by querying the rendered surface rather than by collecting
 * refs. A row can be wrapped by the consumer's `renderItem` — typically a
 * router link, which clones the row and claims its ref — so a ref-based
 * registry would fight the router for the same slot.
 */

const MENU_ITEM_SELECTOR = '[role="menuitem"]'

/** Focusable rows of a menu surface, in DOM order. */
export const queryMenuItems = (container: HTMLElement | null): HTMLElement[] =>
  Array.from(container?.querySelectorAll<HTMLElement>(MENU_ITEM_SELECTOR) ?? [])

/** Index of the row currently holding focus, or -1. */
export const activeItemIndex = (items: HTMLElement[]): number =>
  items.indexOf(document.activeElement as HTMLElement)

/** Moves focus to `index`, wrapping at both ends. */
export const focusItemAt = (
  container: HTMLElement | null,
  index: number
): void => {
  const items = queryMenuItems(container)
  if (!items.length) return
  const wrapped = ((index % items.length) + items.length) % items.length
  items[wrapped]?.focus()
}

/**
 * Focuses whatever inside the trigger the user could have tabbed to. The
 * trigger is a consumer-supplied render prop, so it may be a button or a div
 * carrying button semantics — match either rather than assuming.
 */
export const focusTrigger = (trigger: HTMLElement | null): void => {
  trigger
    ?.querySelector<HTMLElement>(
      'button, a[href], [tabindex]:not([tabindex="-1"])'
    )
    ?.focus()
}

/** The row whose submenu is currently open. */
export const queryExpandedRow = (
  container: HTMLElement | null
): HTMLElement | null =>
  container?.querySelector<HTMLElement>(
    `${MENU_ITEM_SELECTOR}[aria-expanded="true"]`
  ) ?? null
